import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PropertyGeolocationType, PropertyLocationDto } from '@workspace/data-types';
import { CountryIso2Pipe, OpenStreetMapComponent, OsmPoi } from '@workspace/ng-aura';
import { EtxeEnumDisplayPipe } from '../../../pipes/etxe-enum-display.pipe';
import { fetchOsmPois, POI_CATEGORIES, PoiCategoryConfig, PoiCategoryId } from '../../../utils/osm-poi.utils';

@Component({
  selector: 'etxe-property-location-display',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    OpenStreetMapComponent,
    CountryIso2Pipe,
    EtxeEnumDisplayPipe,
  ],
  templateUrl: './location-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyLocationDisplayComponent {
  location = input.required<PropertyLocationDto>();
  hideAddress = input<boolean>(false);
  showPoiToolbar = input<boolean>(true);
  showProximityList = input<boolean>(true);
  isOwner = input<boolean>(true);
  showMarker = input<boolean | undefined>(undefined);

  effectiveShowMarker = computed(() => this.showMarker() ?? this.isOwner());

  categories: PoiCategoryConfig[] = POI_CATEGORIES;
  activeCategories = signal<string[]>(['shops', 'education', 'transport', 'health', 'leisure', 'services']);
  pois = signal<OsmPoi[]>([]);
  loadingPois = signal<boolean>(false);

  allActive = computed(() => this.activeCategories().length === this.categories.length);
  noneActive = computed(() => this.activeCategories().length === 0);

  nearestActivePois = computed(() => {
    const active = this.activeCategories();
    return this.pois()
      .filter((p) => active.includes(p.category))
      .slice(0, 6);
  });

  categoryCounts = computed(() => {
    const counts: Record<string, number> = {};
    this.categories.forEach((c) => (counts[c.id] = 0));
    this.pois().forEach((p) => {
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  });

  getWalkMinutes(meters?: number): number {
    if (!meters) return 1;
    return Math.max(1, Math.round(meters / 80));
  }

  constructor() {
    effect(() => {
      const loc = this.location();
      if (!loc) return;

      const existing = loc.pois;
      const isFake =
        Array.isArray(existing) &&
        existing.some((p) => p.id?.startsWith('fallback-') || p.id?.startsWith('backend-fallback-'));

      if (existing && existing.length > 0 && !isFake) {
        this.pois.set(existing as OsmPoi[]);
        this.loadingPois.set(false);
        return;
      }

      const lat = loc.coordinates?.latitude;
      const lng = loc.coordinates?.longitude;

      if (lat && lng) {
        void this.loadPois(lat, lng);
      }
    });
  }

  private async loadPois(lat: number, lng: number) {
    this.loadingPois.set(true);
    try {
      const result = await fetchOsmPois(lat, lng, 1000);
      this.pois.set(result);
    } catch {
      this.pois.set([]);
    } finally {
      this.loadingPois.set(false);
    }
  }

  toggleCategory(id: PoiCategoryId) {
    const current = this.activeCategories();
    if (current.includes(id)) {
      this.activeCategories.set(current.filter((c) => c !== id));
    } else {
      this.activeCategories.set([...current, id]);
    }
  }

  toggleAll() {
    if (this.allActive()) {
      this.activeCategories.set([]);
    } else {
      this.activeCategories.set(this.categories.map((c) => c.id));
    }
  }

  isCategoryActive(id: string): boolean {
    return this.activeCategories().includes(id);
  }

  getMapRadius(type?: PropertyGeolocationType | null): number {
    switch (type) {
      case PropertyGeolocationType.NEIGHBORHOOD:
        return 800;
      case PropertyGeolocationType.CITY:
        return 2000;
      case PropertyGeolocationType.REGION:
        return 10000;
      default:
        return 800;
    }
  }

  getCategoryDisplayCount(categoryId: string): string {
    const count = this.categoryCounts()[categoryId] || 0;
    return count >= 20 ? '20+' : `${count}`;
  }
}
